import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { fetchMovies } from '../api/client';
import Poster from './Poster';

/**
 * HeaderSearch — magnifying-glass icon that expands into a live movie search.
 * Fetches the full canonical movie list once on first open, filters locally
 * as you type, and shows a dropdown of matches that navigates to the movie
 * page on click. `mobile` switches to a full-width overlay over the header.
 */
function HeaderSearch({ onMovieClick, mobile }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [movies, setMovies] = useState(null); // null = not fetched yet
  const [loaded, setLoaded] = useState(false);
  const [hover, setHover] = useState(-1);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const expand = () => {
    setOpen(true);
    if (movies === null) {
      setMovies([]); // mark as loading so we only fetch once
      fetchMovies()
        .then(list => { setMovies(Array.isArray(list) ? list : []); setLoaded(true); })
        .catch(() => { setMovies([]); setLoaded(true); });
    }
  };

  const collapse = () => { setOpen(false); setQuery(''); setHover(-1); };

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Click outside closes
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) collapse(); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const q = query.trim().toLowerCase();
  const results = q && movies
    ? movies.filter(m =>
        (m.title_he || '').toLowerCase().includes(q) ||
        (m.title_en || '').toLowerCase().includes(q) ||
        (m.title || '').toLowerCase().includes(q)
      ).slice(0, 8)
    : [];

  const pick = (m) => { collapse(); onMovieClick(m.id); };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') collapse();
    else if (e.key === 'Enter' && results.length) pick(results[hover >= 0 ? hover : 0]);
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHover(h => Math.min(results.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHover(h => Math.max(0, h - 1)); }
  };

  const iconBtn = (
    <button onClick={expand} title="חיפוש סרט" style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: 4,
      display: 'flex', alignItems: 'center', color: 'var(--muted)',
    }}>
      <Search size={mobile ? 19 : 18} />
    </button>
  );

  const dropdown = open && q && (
    <div style={{
      position: 'absolute', top: 'calc(100% + 8px)', right: 0, left: mobile ? 0 : 'auto',
      width: mobile ? 'auto' : 320, maxHeight: 420, overflowY: 'auto',
      background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 12,
      boxShadow: 'var(--shadow)', zIndex: 500, direction: 'rtl',
    }}>
      {results.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--faint)' }}>
          {loaded ? 'לא נמצאו סרטים' : 'טוען סרטים...'}
        </div>
      ) : results.map((m, i) => (
        <div key={m.id}
          onClick={() => pick(m)}
          onMouseEnter={() => setHover(i)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
            background: hover === i ? 'var(--surf-hov)' : 'transparent',
            borderBottom: i < results.length - 1 ? '1px solid var(--brd)' : 'none',
          }}>
          <Poster posterUrl={m.poster_url} genre={m.genre} letter={(m.title_he || m.title || '?')[0]} size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--txt)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.title_he || m.title}
            </div>
            {m.title_en && (
              <div style={{ fontSize: 11, color: 'var(--faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.title_en}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const input = (
    <input
      ref={inputRef}
      value={query}
      onChange={e => { setQuery(e.target.value); setHover(-1); }}
      onKeyDown={onKeyDown}
      placeholder="חיפוש סרט..."
      style={{
        background: 'var(--surf-alt)', border: '1px solid var(--brd)', borderRadius: 8,
        padding: '6px 10px', color: 'var(--txt)', fontSize: 13, fontFamily: 'Heebo, sans-serif',
        outline: 'none', direction: 'rtl', width: '100%', boxSizing: 'border-box',
      }}
    />
  );

  if (mobile) {
    // Collapsed: just the icon. Expanded: full-width bar overlaying the header row.
    return (
      <div ref={rootRef} style={{ display: 'flex', alignItems: 'center' }}>
        {!open && iconBtn}
        {open && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 250,
            background: 'var(--surf)', display: 'flex', alignItems: 'center',
            gap: 8, padding: '0 12px',
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              {input}
              {dropdown}
            </div>
            <button onClick={collapse} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: 'var(--muted)' }}>
              <X size={20} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop: icon expands to an inline input with a width transition.
  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: open ? 240 : 0, overflow: open ? 'visible' : 'hidden', transition: 'width .18s ease', position: 'relative' }}>
        {input}
        {dropdown}
      </div>
      {open ? (
        <button onClick={collapse} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', color: 'var(--muted)' }}>
          <X size={16} />
        </button>
      ) : iconBtn}
    </div>
  );
}

export default HeaderSearch;
